import React, { useRef, useEffect, useState } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm         from '../../components/RSVPForm';
import Wishlist         from '../../components/Wishlist';
import Countdown        from '../../components/Countdown';
import Schedule         from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton   from '../../components/CalendarButton';
import FixedCTA         from '../../components/FixedCTA';
import YandexMap        from '../../components/YandexMap';
import useReveal        from '../../components/useReveal';
import BlockSubtitle    from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Graphite.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const src = u => !u ? null : (u.startsWith('http') ? u : u);
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const MON_SHORT = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];
const MON_FULL  = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];

const fmtDateFull = raw => {
  if (!raw) return '?? ??? ???? ГОДА';
  const d = new Date(raw);
  return `${d.getDate()} ${MON_FULL[d.getMonth()].toUpperCase()} ${d.getFullYear()} ГОДА`;
};

const getAdjacentDays = raw => {
  if (!raw) {
    return [
      { num: '30', mon: 'апр' },
      { num: '01', mon: 'мая' },
      { num: '02', mon: 'мая' },
      { num: '03', mon: 'мая' },
      { num: '04', mon: 'мая' },
    ];
  }
  const wd = new Date(raw);
  return [-2, -1, 0, 1, 2].map(offset => {
    const d = new Date(wd);
    d.setDate(wd.getDate() + offset);
    return { num: String(d.getDate()).padStart(2, '0'), mon: MON_SHORT[d.getMonth()] };
  });
};

/* Load template fonts once. */
function useGrFonts() {
  useEffect(() => {
    const id = 'gr-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,700&family=Great+Vibes&family=Montserrat:wght@300;400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* ── Heart divider ───────────────────────────────────────────── */
function HeartDivider({ dark }) {
  return (
    <div className={`gr-hd${dark ? ' gr-hd--dark' : ''}`}>
      <div className="gr-hd-line" />
      <svg className="gr-hd-icon" viewBox="0 0 12 11" fill="currentColor">
        <path d="M6 10.5C6 10.5 0.5 7 0.5 3.5C0.5 1.57 2.07 0 4 0C5.03 0 5.96 0.47 6 1.5C6.04 0.47 6.97 0 8 0C9.93 0 11.5 1.57 11.5 3.5C11.5 7 6 10.5 6 10.5Z"/>
      </svg>
      <div className="gr-hd-line" />
    </div>
  );
}

/* ── Nav ─────────────────────────────────────────────────────── */
function GrNav({ blocks, menu }) {
  const links = [
    blocks.includes('schedule') && { href: '#gr-schedule',  label: menu?.schedule || 'Программа' },
    blocks.includes('map')      && { href: '#gr-location',  label: menu?.location || 'Локация'   },
    blocks.includes('palette')  && { href: '#gr-dresscode', label: menu?.dresscode || 'Дресс-код' },
    blocks.includes('rsvp')     && { href: '#gr-rsvp',      label: menu?.rsvp || 'Анкета'    },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <nav className="gr-nav">
      {links.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}
    </nav>
  );
}

/* ── Schedule with heart dividers ───────────────────────────── */
function GrScheduleList({ items }) {
  return (
    <div className="gr-sched">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <HeartDivider />}
          <div className="gr-sched-row">
            <span className="gr-sched-time">{item.time}</span>
            <div className="gr-sched-info">
              <span className="gr-sched-name">{item.name || item.event}</span>
              {(item.desc || item.subtitle || item.description) && (
                <span className="gr-sched-desc">
                  {item.desc || item.subtitle || item.description}
                </span>
              )}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Class maps for shared components ───────────────────────── */
const GR_DRESS_CLASSES = {
  wrap: 'gr-swatches',
  swatchWrap: 'gr-swatch-wrap',
  swatch: 'gr-swatch',
  swatchLabel: 'gr-swatch-lbl',
  photos: 'gr-dc-photos',
  photo: 'gr-dc-photo',
};
const GR_CD_CLASSES = {
  root: 'gr-cd',
  cell: 'gr-cd-cell',
  num: 'gr-cd-num',
  label: 'gr-cd-lbl',
};
const GR_RSVP_CLASSES = {
  form:    'gr-form',
  tabs:    'gr-rsvp-tabs',
  tab:     'gr-rsvp-tab',
  tabSel:  'gr-rsvp-tab--sel',
  field:   'gr-form-field',
  label:   'gr-form-lbl',
  input:   'gr-form-input',
  submit:  'gr-btn gr-btn--primary gr-btn--full',
  success: 'gr-form-ok',
  error:   'gr-form-err',
  chip:    'gr-chip',
  chipSel: 'gr-chip--sel',
  chips:   'gr-form-chips',
};
const GR_WL_CLASSES = {
  list: 'gr-wish',
  item: 'gr-wish-item',
  itemBorder: '',
  row: 'gr-wish-row',
  name: 'gr-wish-name',
  price: 'gr-wish-price',
  btn: 'gr-btn gr-btn--invert gr-btn--full',
  btnReserved: 'gr-btn--reserved',
};
const GR_WL_MODAL_CLASSES = {
  bg:         'gr-wish-modal-bg',
  modal:      'gr-wish-modal',
  title:      'gr-wish-modal-title',
  subtitle:   'gr-wish-modal-sub',
  input:      'gr-form-input',
  error:      'gr-form-err',
  btns:       'gr-wish-modal-btns',
  btnCancel:  'gr-btn gr-btn--outline',
  btnConfirm: 'gr-btn gr-btn--primary',
};

/* ══ MAIN ══════════════════════════════════════════════════════ */
export default function Graphite({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.gr-reveal', inClass: 'gr-in' });
  useGrFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};
  const bc = p.block_content || {};

  const blocks = p.blocks || ['menu','countdown','schedule','map','palette','rsvp','survey','message','wishlist','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');
  const HAS_RSVP     = blocks.includes('rsvp');

  const bride    = p.bride  || 'Александра';
  const groom    = p.groom  || 'Богдан';
  const tagline  = bc.header?.subtitle || p.wedding_slogan || 'Не прошло и 5-ти лет';
  const invTitle = bc.message?.title   || 'Дорогие наши!';
  const invText  = p.invitation_text   || bc.message?.text || '';
  const footerQuote = bc.footer?.quote || p.wedding_slogan || 'Одна любовь,\nдругой такой\nи не было';

  const photos    = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto = src(photos[0]?.url) || null;
  const heroMeta  = photos[0] || null;
  const dcPhotos  = p.dresscode_photos || [];
  /* Футер-фото независимо от главного: если не задано — футер без фото. */
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  const dateLabel = fmtDateFull(p.wedding_date);
  const tiles = getAdjacentDays(p.wedding_date);

  /* Пусто (пользователь удалил все пункты) → показываем только заголовок и
     подзаголовок, без дефолтных пунктов. */
  const schedule = p.schedule?.length ? p.schedule : [];

  /* Показываем venue с именем ИЛИ адресом (имя без адреса — без карты). */
  const venues   = (p.venues || []).filter(v => (v.name || '').trim() || (v.address || '').trim());
  const venuesAddr = venues.filter(v => (v.address || '').trim());
  const wishlist = p.wishlist || [];

  const swatches = p.dresscode_palette || [];

  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  const HERO_INITIAL_A = (bride?.[0] || 'А').toUpperCase();
  const HERO_INITIAL_B = (groom?.[0] || 'Б').toUpperCase();

  /* Palette overrides — the user-selected project/design palette replaces the
     default monochrome scheme. Graphite uses `--gr-dark` for all black
     surfaces (nav, dark sections, buttons, text) and `--gr-light` for the
     light surfaces. Map primary/secondary/accent to these CSS vars. */
  const pal = { ...(d.palette || {}), ...(p.palette || {}) };
  const palStyle = {};
  if (pal.primary)   palStyle['--gr-dark']  = pal.primary;
  if (pal.secondary) palStyle['--gr-light'] = pal.secondary;
  if (pal.accent)    palStyle['--gr-nav']   = pal.accent;
  if (pal.textDark)  palStyle['--gr-text']  = pal.textDark;

  return (
    <div className={`gr-page${inPreview ? ' gr-page--preview' : ''}`} ref={pageRef} style={palStyle}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />
      {HAS_MENU && <GrNav blocks={blocks} menu={bc.menu} />}

      {/* ── HERO: initials + names + date, then photo (mobile)
             OR split 50/50 with photo on right (desktop) ───── */}
      <section className="gr-hero gr-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        <div className="gr-hero-text">
          <div className="gr-hero-initials">
            <span className="gr-initial">{HERO_INITIAL_A}</span>
            <svg className="gr-hero-heart" viewBox="0 0 12 11" fill="currentColor" aria-hidden="true">
              <path d="M6 10.5C6 10.5 0.5 7 0.5 3.5C0.5 1.57 2.07 0 4 0C5.03 0 5.96 0.47 6 1.5C6.04 0.47 6.97 0 8 0C9.93 0 11.5 1.57 11.5 3.5C11.5 7 6 10.5 6 10.5Z"/>
            </svg>
            <span className="gr-initial">{HERO_INITIAL_B}</span>
          </div>
          <p className="gr-hero-names">{bride} {bc.header?.amp || '+'} {groom}</p>
          <div className="gr-hero-date-wrap">
            <p className="gr-hero-date">{dateLabel}</p>
            {tagline && <p className="gr-hero-tagline">{tagline}</p>}
          </div>
        </div>
        {heroPhoto && (
          <div className="gr-hero-photo">
            <img src={heroPhoto} alt="" style={{ filter: flt(heroMeta), objectPosition: objPos(heroMeta) }} />
          </div>
        )}
      </section>

      {/* ── INVITE (dark) ────────────────────────────────────── */}
      <section className="gr-invite gr-reveal">
        <h2 className="gr-invite-title">{invTitle}</h2>
        <div className="gr-invite-body">
          {invText
            ? invText.split('\n').map((line, i) =>
                line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
            : <>
                <p>С огромным волнением и радостью хотим</p>
                <p>пригласить вас на ужин в честь нашего</p>
                <p>бракосочетания!</p>
                <br />
                <p>В этот особенный день мы будем счастливы видеть каждого из вас, чтобы разделить волшебство момента и создать воспоминания, которые останутся в наших сердцах навсегда.</p>
              </>}
        </div>
      </section>

      {/* ── DATE TILES + Calendar button ─────────────────────── */}
      <section className="gr-date-sect gr-reveal">
        <div className="gr-date-tiles">
          {tiles.map((day, i) => (
            <div key={i} className={`gr-tile${i === 2 ? ' gr-tile--active' : ''}`}>
              <span className="gr-tile-num">{day.num}</span>
              <span className="gr-tile-mon">{day.mon}</span>
            </div>
          ))}
        </div>
        {HAS_CALENDAR && (
          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="gr-btn gr-btn--primary gr-btn--cal">
            {calBtn}
          </CalendarButton>
        )}
      </section>

      {/* ── Dynamic blocks in Figma order ────────────────────── */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              /* Блок скрывается ТОЛЬКО тумблером (фильтр blocks выше). Здесь не
                 прячем по пустоте — показываем заголовок+подзаголовок всегда. */
              return (
                <section key="map" className="gr-sect gr-map-sect gr-reveal" id="gr-location">
                  <div className="gr-map-left">
                    <h2 className="gr-h gr-h--italic gr-h--white">{titleLocation}</h2>
                    <BlockSubtitle text={bc.map?.subtitle} className="gr-p" />
                    <div className="gr-addr-list">
                      {venues.map((v, i) => (
                        <div key={i} className="gr-addr">
                          <svg className="gr-addr-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                            <path d="M8 0C4.69 0 2 2.69 2 6c0 4.5 6 10 6 10s6-5.5 6-10c0-3.31-2.69-6-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"/>
                          </svg>
                          <span className="gr-addr-text">
                            {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                          </span>
                        </div>
                      ))}
                      {!venues.length && p.wedding_address && (
                        <div className="gr-addr">
                          <svg className="gr-addr-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                            <path d="M8 0C4.69 0 2 2.69 2 6c0 4.5 6 10 6 10s6-5.5 6-10c0-3.31-2.69-6-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"/>
                          </svg>
                          <span className="gr-addr-text">{p.wedding_address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Карта — только если есть адрес (имя без адреса → без карты). */}
                  {(p.map_url || venuesAddr.length > 0 || (p.wedding_address || '').trim()) && (
                  <div className="gr-map-wrap">
                    {(() => {
                      if (p.map_url) return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} />;
                      const withCoords = venuesAddr.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#darkIcon" />;
                      }
                      const addr = venuesAddr.length > 0
                        ? venuesAddr.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="gr-sect gr-sched-sect gr-reveal" id="gr-schedule">
                  <div className="gr-sect-left">
                    <h2 className="gr-h">{titleSchedule}</h2>
                    <BlockSubtitle text={bc.schedule?.subtitle} className="gr-p" />
                  </div>
                  <div className="gr-sect-right">
                    <GrScheduleList items={schedule} />
                  </div>
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="gr-sect gr-dress-sect gr-reveal" id="gr-dresscode">
                  <div className="gr-dress-top">
                    <div className="gr-dress-top-text">
                      <h2 className="gr-h gr-h--white">{titleDress}</h2>
                      <BlockSubtitle text={bc.palette?.subtitle} className="gr-dress-desc" />
                    </div>
                    <DresscodePalette
                      swatches={swatches}
                      photos={[]}
                      classes={GR_DRESS_CLASSES}
                    />
                  </div>
                  {dcPhotos.length > 0 && (
                    <DresscodePalette
                      swatches={[]}
                      photos={dcPhotos}
                      cssFilter={flt}
                      classes={GR_DRESS_CLASSES}
                    />
                  )}
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="gr-sect gr-rsvp-sect gr-reveal" id="gr-rsvp">
                  <div className="gr-sect-left">
                    <h2 className="gr-h gr-h--bold">{titleRsvp}</h2>
                    <BlockSubtitle text={bc.rsvp?.subtitle} className="gr-p" />
                  </div>
                  <div className="gr-sect-right">
                    <RSVPForm project={p} blocks={blocks} classes={GR_RSVP_CLASSES} />
                  </div>
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="gr-sect gr-wish-sect gr-reveal">
                  <div className="gr-sect-left">
                    <h2 className="gr-h gr-h--white gr-h--bold">{titleWish}</h2>
                    <BlockSubtitle text={bc.wishlist?.subtitle} className="gr-wish-desc" />
                  </div>
                  <div className="gr-sect-right">
                    <Wishlist
                      items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                      slug={p.slug}
                      classes={GR_WL_CLASSES}
                      modalClasses={GR_WL_MODAL_CLASSES}
                    />
                  </div>
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="gr-sect gr-cd-sect gr-reveal">
                  <div className="gr-sect-left">
                    <h2 className="gr-h">{titleCD}</h2>
                    <BlockSubtitle text={bc.countdown?.subtitle} className="gr-p" />
                  </div>
                  <div className="gr-sect-right">
                    <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={GR_CD_CLASSES} withColons />
                  </div>
                </section>
              );

            default:
              return null;
          }
        })}

      {/* ── FOOTER: big photo + black quote panel ─────────────── */}
      <footer className="gr-footer gr-reveal" data-nophoto={footerPhoto ? undefined : '1'}>
        {footerPhoto && (
          <div className="gr-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="gr-footer-quote">
          <div className="gr-footer-quote-text">
            {footerQuote.split('\n').map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </div>
      </footer>

      {/* ── Fixed CTA ─────────────────────────────────────────── */}
      {HAS_RSVP && (
        <FixedCTA
          href="#gr-rsvp"
          targetId="gr-rsvp"
          scrollRoot={pageRef}
          className="gr-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
